import { PENDING_POLICY_EXCEPTION } from "@/const/policy-exception.constants"
import { getIssuesCountMapOrderedSeverity } from "@/domain/vulnerability"
import {
  DEFAULT_LONG_FORMAT,
  DURATION_KEYS,
  getDateFromSeconds,
  getDateInUserLocale,
  getDuration
} from "@/helpers/date.helpers"
import {
  getInputFilter,
  getMultipleValuesFilter
} from "@/helpers/filters.helper"

import type {
  FilterScanHistoryResponse,
  PolicyResultTopInitiators,
  ScanHistoryResponse,
  ScanHistoryStatisticsResponse,
  ScannerType
} from "@/api/useScanHistory.types"
import type { TFunction } from "@/hooks/useTranslation"
import type { CountedOption } from "@/organisms/FilterNew/CountedCheckboxFilter"

export const getScanHistorySelector = (response: ScanHistoryResponse) => {
  const { data, ...restResponse } = response || {}

  const getPolicyStatus = (
    policyResult: ScanHistoryResponse["data"][number]["policyResult"]
  ) => {
    switch (policyResult) {
      case "passed":
        return "success"
      case "failed":
        return "failed"
      case "warn_and_pass":
      case "skipped":
        return "warning"
      case "scanning":
        return "pending"
      default:
        return "neutral"
    }
  }

  return {
    data: (data || []).map((item) => {
      const { issuesSummary, ...rest } = item
      const startDate = getDateFromSeconds(item.startTime)
      const endDate = getDateFromSeconds(item.endTime)
      const duration = getDuration(startDate, endDate)
      const isScanning = rest.scanStatus === "scanning"

      return {
        key: item.scanId,
        severity: isScanning
          ? []
          : getIssuesCountMapOrderedSeverity(issuesSummary),
        shortScanId: item.scanId.slice(0, 8),
        scanTypeKey: `governance.scanHistory.scanType.${item.scanType}`,
        scanDate: getDateInUserLocale(
          getDateFromSeconds(item.startTime),
          DEFAULT_LONG_FORMAT
        ),
        scanDateTooltip: {
          startDate: getDateInUserLocale(startDate, DEFAULT_LONG_FORMAT),
          endDate: isScanning
            ? "-"
            : getDateInUserLocale(endDate, DEFAULT_LONG_FORMAT),
          duration: DURATION_KEYS.filter((key) =>
            isScanning ? false : !!duration.get(key)
          ).map((key) => ({
            key,
            count: duration.get(key)
          }))
        },
        ghostCreatorCell: true,
        policyStatus: getPolicyStatus(item.policyResult),
        // Reviewed while its request waits on an admin; once decided, it links its report like any scan.
        isPolicyExceptionToReview:
          item.policyExceptionReviewable &&
          item.policyExceptionStatus === PENDING_POLICY_EXCEPTION,
        ...rest
      }
    }),
    ...restResponse
  }
}

export const getFiltersKeys = () => [
  "resourceScanned",
  "initiator",
  "refId",
  "policyName",
  "policyResult",
  "scanType",
  "policyExceptionStatus"
]

export const getFiltersScanHistorySelector = (
  response: FilterScanHistoryResponse,
  t: TFunction
) => {
  const { data, ...restResponse } = response || {}
  const getOptions = (data: string[], key: string) =>
    (data || []).map((value) => ({
      label: key ? t(`${key}.${value}`) : t(`${value}`),
      value
    }))

  const policyResult = getMultipleValuesFilter("policyResult", t, {
    options: getOptions(
      data["policyResult"].filter((value) => value !== "skipped"),
      "governance.scanHistory.status"
    )
  })

  const scanType = getMultipleValuesFilter("scanType", t, {
    options: getOptions(data["scanType"], "governance.scanHistory.scanType")
  })

  const policyName = getMultipleValuesFilter("policyName", t, {
    options: getOptions(data["policyName"], "")
  })

  // Offered only while the tenant has policy exceptions on
  const policyExceptionStatus =
    data.policyExceptionStatus &&
    getMultipleValuesFilter("policyExceptionStatus", t, {
      options: getOptions(
        data.policyExceptionStatus,
        "governance.scanHistory.policyExceptionStatus"
      ).map((option): CountedOption =>
        option.value === PENDING_POLICY_EXCEPTION
          ? { ...option, count: data.pendingPolicyExceptionRequests }
          : option
      )
    })

  const filters = getFiltersKeys().flatMap((key) => {
    if (key === "policyResult") return policyResult
    if (key === "scanType") return scanType
    if (key === "policyName") return policyName
    if (key === "policyExceptionStatus") return policyExceptionStatus || []

    return getInputFilter(key, t)
  })

  return {
    data: {
      policyResult,
      scanType,
      policyExceptionStatus,
      all: filters
    },
    ...restResponse
  }
}

export const getTopInitiatorsByPolicyNameSelector = (
  response: ScanHistoryStatisticsResponse,
  filters: [PolicyResultTopInitiators, ScannerType]
) => {
  const { data, ...restResponse } = response || {}
  const { topInitiators = [] } = data || {}
  const [policyResult, scanner] = filters
  const isPassedPolicy = policyResult === "passed"

  const filteredTopInitiators = (
    initiators: typeof topInitiators,
    filter: [PolicyResultTopInitiators, ScannerType]
  ) =>
    initiators.find(
      (initiator) =>
        initiator.policyResult === filter[0] && initiator.scanner === filter[1]
    )?.initiators || []

  const currentTopInitiators = filteredTopInitiators(topInitiators, filters)
  const aggregateData = isPassedPolicy
    ? Object.entries(
        [
          ...filteredTopInitiators(topInitiators, ["scanned", scanner]),
          ...currentTopInitiators
        ].reduce(
          (acc, { initiator, count }) => {
            acc[initiator] = (acc[initiator] || 0) + count

            return acc
          },
          {} as Record<string, number>
        )
      )
        .map(([initiator, count]) => ({ initiator, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5)
    : currentTopInitiators

  return {
    data: aggregateData.map(({ count, ...rest }) => ({
      count,
      scanner,
      key: `${rest.initiator}_${count}`,
      ...rest
    })),
    ...restResponse
  }
}

export const getCountsOverTimeScanHistoryStatisticsSelector = (
  response: ScanHistoryStatisticsResponse
) => {
  const { data, metadata, ...restResponse } = response || {}
  const { countsOverTime = [] } = data || {}
  const types = ["failed", "skipped", "warn_and_pass", "passed"] as const
  const lastValueByTimestamp: Record<string, string> = {}
  const getType = (type: (typeof types)[number]) =>
    type === "skipped" ? "warn_and_pass" : type
  const nextData = countsOverTime.reduce(
    (acc, { timestamp, ...items }) => {
      const _timestamp = timestamp * 1000

      types.forEach((type) => {
        let value = items[type] || 0

        if (type === "passed") value += items["scanned"] || 0

        if (!!value && !lastValueByTimestamp[_timestamp])
          lastValueByTimestamp[_timestamp] = getType(type)

        acc.push({
          type: getType(type),
          value,
          timestamp: _timestamp
        })
      })

      return acc
    },
    [] as { type: string; value: number; timestamp: number }[]
  )

  return {
    data: nextData,
    metadata: { lastValueByTimestamp, ...metadata },
    ...restResponse
  }
}
